import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const FarmSelect = ({ value, onChange, farms, testId }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value || null)} className="bg-white border border-forest/20 rounded-xl px-3 py-2 font-manrope text-sm w-full" data-testid={testId}>
    <option value="">Source Coming Soon (no farm confirmed)</option>
    {farms.map((f) => <option key={f.farm_id} value={f.farm_id}>{`${f.name} — ${f.town || 'NH'}`}</option>)}
  </select>
);

function BoxTierCard({ tier, farms, onSave }) {
  const [form, setForm] = useState(tier);
  useEffect(() => setForm(tier), [tier]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="bg-white rounded-2xl border border-forest/10 p-6" data-testid={`pricing-box-${tier.key}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-playfair text-xl font-bold text-dark">{tier.name}</h3>
        {form.cost_warning && (
          <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2.5 py-1 rounded-full" data-testid={`cost-warning-${tier.key}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> Over cost target
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div><Label className="text-xs">Retail price ($/delivery)</Label><Input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} className="bg-cream border-forest/20" data-testid={`price-${tier.key}`} /></div>
        <div><Label className="text-xs">Max farm cost target ($)</Label><Input type="number" value={form.max_farm_cost} onChange={(e) => set('max_farm_cost', e.target.value)} className="bg-cream border-forest/20" data-testid={`max-cost-${tier.key}`} /></div>
        <div><Label className="text-xs">Planned farm cost ($)</Label><Input type="number" value={form.planned_farm_cost} onChange={(e) => set('planned_farm_cost', e.target.value)} className="bg-cream border-forest/20" data-testid={`planned-cost-${tier.key}`} /></div>
        <div><Label className="text-xs">Weight capacity (lb)</Label><Input type="number" value={form.weight_capacity_lb} onChange={(e) => set('weight_capacity_lb', e.target.value)} className="bg-cream border-forest/20" data-testid={`weight-cap-${tier.key}`} /></div>
      </div>
      <p className="text-sm font-manrope text-dark/60 mb-3">Margin: <strong className="text-dark">${form.margin} ({form.margin_pct}%)</strong> · Item guideline {form.item_count_min}-{form.item_count_max}</p>
      <div className="mb-4">
        <Label className="text-xs">Confirmed farm source</Label>
        <FarmSelect value={form.farm_id} onChange={(v) => set('farm_id', v)} farms={farms} testId={`farm-select-${tier.key}`} />
      </div>
      <button onClick={() => onSave(tier.key, form)} className="btn-primary py-2 px-5 text-sm" data-testid={`save-box-${tier.key}`}>Save</button>
    </div>
  );
}

function AddonRow({ addon, farms, minMargin, onSave }) {
  const [form, setForm] = useState(addon);
  useEffect(() => setForm(addon), [addon]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <tr data-testid={`pricing-addon-${addon.product_id}`}>
      <td className="px-4 py-3 font-manrope font-semibold text-dark">{addon.name}</td>
      <td className="px-4 py-2"><Input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} className="bg-cream border-forest/20 w-24" data-testid={`addon-price-${addon.product_id}`} /></td>
      <td className="px-4 py-2"><Input type="number" value={form.farm_cost} onChange={(e) => set('farm_cost', e.target.value)} className="bg-cream border-forest/20 w-24" data-testid={`addon-cost-${addon.product_id}`} /></td>
      <td className="px-4 py-3 font-manrope text-sm">
        <span className={form.margin_pct < minMargin * 100 ? 'text-red-600 font-bold' : 'text-forest font-bold'}>{form.margin_pct}%</span>
        {form.below_min && <span className="block text-xs text-red-500">Below {Math.round(minMargin * 100)}% target</span>}
      </td>
      <td className="px-4 py-2 min-w-[220px]"><FarmSelect value={form.farm_id} onChange={(v) => set('farm_id', v)} farms={farms} testId={`addon-farm-${addon.product_id}`} /></td>
      <td className="px-4 py-2 text-center">
        <input
          type="checkbox"
          checked={!!form.referral_reward_eligible}
          onChange={(e) => set('referral_reward_eligible', e.target.checked)}
          className="w-4 h-4 accent-forest"
          data-testid={`addon-referral-toggle-${addon.product_id}`}
        />
      </td>
      <td className="px-4 py-2"><button onClick={() => onSave(addon.product_id, form)} className="btn-secondary py-1.5 px-4 text-xs" data-testid={`save-addon-${addon.product_id}`}>Save</button></td>
    </tr>
  );
}

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [farms, setFarms] = useState([]);
  const [minMarginInput, setMinMarginInput] = useState('');

  const load = useCallback(async () => {
    const [p, f] = await Promise.all([api.get('/admin/pricing'), api.get('/admin/roster-farms')]);
    setData(p.data);
    setFarms(f.data);
    setMinMarginInput(p.data.addon_min_margin_pct);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBox = async (key, form) => {
    await api.put(`/admin/pricing/box/${key}`, {
      price: parseFloat(form.price), max_farm_cost: parseFloat(form.max_farm_cost),
      planned_farm_cost: parseFloat(form.planned_farm_cost), weight_capacity_lb: parseFloat(form.weight_capacity_lb),
      farm_id: form.farm_id,
    });
    toast.success(`${form.name} pricing updated`);
    load();
  };

  const saveAddon = async (productId, form) => {
    const res = await api.put(`/admin/pricing/addon/${productId}`, {
      price: parseFloat(form.price), farm_cost: parseFloat(form.farm_cost), farm_id: form.farm_id,
      referral_reward_eligible: !!form.referral_reward_eligible,
    });
    if (res.data.below_min) toast.warning(`Margin is below the ${Math.round((data.addon_min_margin) * 100)}% target`);
    else toast.success('Add-on pricing updated');
    load();
  };

  const saveMinMargin = async () => {
    await api.put('/admin/pricing/settings', { addon_min_margin: parseFloat(minMarginInput) });
    toast.success('Minimum margin threshold updated');
    load();
  };

  if (!data) return null;

  return (
    <div data-testid="admin-pricing">
      <p className="font-manrope text-dark/60 mb-6">Core box tiers, add-on margins, and confirmed farm sourcing. Retail price changes here are the only way prices change — supplier cost changes never auto-update what customers pay.</p>

      <h2 className="font-playfair text-xl font-bold text-dark mb-4">Box Tiers</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {data.box_tiers.map((t) => <BoxTierCard key={t.key} tier={t} farms={farms} onSave={saveBox} />)}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-playfair text-xl font-bold text-dark">Add-Ons</h2>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Min margin target (%)</Label>
          <Input type="number" value={minMarginInput} onChange={(e) => setMinMarginInput(e.target.value)} className="bg-white border-forest/20 w-20" data-testid="min-margin-input" />
          <button onClick={saveMinMargin} className="btn-secondary py-2 px-4 text-xs" data-testid="save-min-margin">Save</button>
        </div>
      </div>
      <p className="text-xs text-dark/50 font-manrope mb-3">Check "Referral gift" on exactly one add-on &mdash; it's the free item given to a customer when their referral converts (plus their 10% reward). Checking a new one automatically unchecks the previous one.</p>
      <div className="bg-white rounded-2xl border border-forest/10 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-cream"><tr>{['Add-on', 'Price', 'Farm cost', 'Margin', 'Confirmed source', 'Referral gift', ''].map((h) => <th key={h} className="px-4 py-3 text-left font-manrope text-sm font-semibold text-dark/60">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-forest/10">
            {data.addons.map((a) => <AddonRow key={a.product_id} addon={a} farms={farms} minMargin={data.addon_min_margin} onSave={saveAddon} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
